import { RadioGroup as AriaKitRadioGroup, RadioState as AriaKitRadioState, useRadioState as useAriaKitRadioState } from "ariakit";
import React from "react";
import styled from "styled-components";
import { useHTMLInputElementValidationProjection } from "../hooks/useHTMLInputElementValidationProjection";
import { PropError, PropLabel } from "../PropX";
import { style } from "../style";
import { RadioGroupStateProvider } from "./RadioGroupStateContext";

const OuterContainer = styled(AriaKitRadioGroup)<{ state: AriaKitRadioState }>`
  all: unset;
  width: 100%;
`;

const RadioGroupContainer = styled.div<{ $horizontal: boolean }>`
  all: unset;

  display: flex;
  align-items: flex-start;
  flex-wrap: wrap;
  flex-direction: ${(props) => (props.$horizontal ? "row" : "column")};
  column-gap: ${style.vars.whitespace.regular};
  row-gap: ${style.vars.whitespace.small};

  width: 100%;
`;

type Props = {
  /**
   * The name of the radio group. Used as the `name` attribute on the radio inputs.
   */
  name: string;

  /**
   * The value of the currently selected radio button.
   */
  selected: string | null;

  onSelect(value: string | null): void;

  /**
   * A label to display above the group.
   * @default undefined
   */
  label?: PropLabel;

  /**
   * A custom error to display for the group. Overrides standard errors created by `required`.
   * @default undefined
   */
  error?: PropError | PropError[];

  /**
   * Whether the entire radio group is disabled.
   * @default false
   */
  disabled?: boolean;

  /**
   * The orientation of the radio buttons.
   * @default "vertical"
   */
  orientation?: "horizontal" | "vertical";

  /**
   * The event for which to validate the input.
   * @default "manual"
   */
  validateOn?: "change" | "blur" | "manual";

  /**
   * Whether selecting a radio button in this group is required.
   * @default false
   */
  required?: boolean;
};

export const RadioGroup: React.FC<Props> = (props) => {
  const { children, name, selected, onSelect, validateOn = "manual", orientation = "vertical", required = false, disabled = false } = props;

  const radioState = useAriaKitRadioState({
    orientation: orientation === "horizontal" ? "vertical" : "horizontal",
    value: selected,
    setValue: (next) => {
      /**
       * ariakit allows numbers to be passed to setValue, but we don't want to allow that,
       * so we filter those out here. Passing a number should never happen anyway, so this
       * is more of a sanity check.
       */
      if (typeof next === "number") {
        return;
      }

      onSelect(next);
    },
  });

  /**
   * We can validate the entire group by attaching the validation to the first radio button only.
   */
  const firstRadioRef = React.useMemo(() => {
    const { items } = radioState;

    if (items.length === 0) return { current: null };

    const [item] = items;

    return item.ref as React.RefObject<HTMLInputElement>;
  }, [radioState.items.length]);

  const [errors, propsForInput] = useHTMLInputElementValidationProjection({ ...props }, { ref: firstRadioRef, validateOn });
  const { invalid } = propsForInput;

  const horizontal = orientation === "horizontal";

  return (
    <OuterContainer as="fieldset" role="radiogroup" state={radioState} disabled={disabled}>
      {props.label && PropLabel.renderForBeforeInput(props.label, { as: "legend", required, invalid })}
      <RadioGroupContainer $horizontal={horizontal}>
        <RadioGroupStateProvider
          name={name}
          selected={selected}
          horizontal={horizontal}
          required={required}
          disabled={disabled}
          radio={radioState}
          {...propsForInput}
        >
          {children}
        </RadioGroupStateProvider>
      </RadioGroupContainer>
      {PropError.renderGroupAfterInput(errors)}
    </OuterContainer>
  );
};

RadioGroup.displayName = "RadioGroup";
